import { t } from 'i18next';
import { useEffect, useState } from 'react';

import InputWithError from '../../../components/Inputs/InputWithError';
import ComponentCircleLoader from '../../../components/Loaders/ComponentCircleLoader';
import MapsAutocompleteDropdown from '../../../components/MapsAutocompleteDropdown/MapsAutocompleteDropdown';
import usePlacesAutocompleteService from '../../../customHooks/usePlacesAutocompleteService';
import { RadiusButton } from '../../../styledComponents/buttons';
import {
  CommonFlex,
  CommonGrid,
  CommonText,
  FormAlert,
  InputLabel,
  InputWithLabelSection,
  RedStar,
} from '../../../styledComponents/common';
import { MapAndAlertContainerForUpdateOffice } from '../../FieldServices/RouteMap/routeMapStyles';
import OfficesMap from '../OfficesMap';
import { InfoSection } from '../officesStyles';
import ItemsRelatedToCampsSection from './ItemsRelatedToCampsSection';
import PdfUpload from './PdfUploadOffice';
import PdfUploadSketch from './PdfUploadOfficeSketch';

const ZoneValues = ({
  saveErrors,
  loadError,
  isLoaded,
  radiusOptions,
  zoneValues,
  setZoneValues,
  activeZone,
  itemsList,
  numberOfPilgrims,
  setSaveErrors,
}) => {
  const [singleAddressMarker, setSingleAddressMarker] = useState(null);
  const [map, setMap] = useState(null);
  const {
    geocoderService,
    getPlacePredictions,
    placePredictions,
    setPlacePredictions,
    isPredictionsLoading,
    refreshSessionToken,
  } = usePlacesAutocompleteService({ map, debounceTimeout: 500 });

  const getGeocode = place => {
    if (geocoderService && place && place.place_id && map) {
      setAddressInput(place.description);
      geocoderService
        .geocode({ placeId: place.place_id })
        .then(({ results }) => {
          const location = results[0].geometry.location;
          handleSetAddressMarker(location);
          setZoneValues(prev => ({
            ...prev,
            [activeZone]: {
              ...prev[activeZone],
              latitude: location.lat(),
              longitude: location.lng(),
            },
          }));
        })
        .catch(e => {
          console.log(e);
        });
      setPlacePredictions([]);
    }
    refreshSessionToken();
  };
  const handleReverseGeocode = coord => {
    if (geocoderService && coord) {
      setPlacePredictions([]);
      geocoderService
        .geocode({ location: coord })
        .then(results => {
          if (results.results && results.results.length) {
            setAddressInput(results.results[0].formatted_address);
          } else {
            setAddressInput(`${coord.lat()}, ${coord.lng()}`);
          }
        })
        .catch(e => {
          console.log(e);
        });
    }
  };
  const handleSetAddressFromMap = location => {
    if (location && location.latLng) {
      handleSetAddressMarker(location.latLng);
      handleReverseGeocode(location.latLng);
      setZoneValues(prev => ({
        ...prev,
        [activeZone]: {
          ...prev[activeZone],
          latitude:
            typeof location.lat === 'function' ? location.lat() : location.lat,
          longitude:
            typeof location.lng === 'function' ? location.lng() : location.lng,
        },
      }));
    }
  };
  const handleSetAddressMarker = location => {
    if (location) {
      setSingleAddressMarker(location);
      handleMapPanAndZoomToSingleLocation(location);
    }
  };
  const handleMapPanAndZoomToSingleLocation = location => {
    if (map) {
      map.panTo(location);
      map.setZoom(17);
    }
  };

  const [imagePreview, setImagePreview] = useState(null);
  const [campNumberArray, setCampNumberArray] = useState(['']);
  const addMoreCampNumber = () => {
    setCampNumberArray([...campNumberArray, '']);
  };
  const addFile = file => {
    setIsImageLoading(true);
    setImagePreview(file[0]);
  };

  const [radiusInput, setRadiusInput] = useState(100);
  const [addressInput, setAddressInput] = useState('');
  const [campArea, setCampArea] = useState('');
  const [mainGates, setMainGates] = useState('');
  const [kitchens, setKitchens] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [items, setItems] = useState([]);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [isImageLoading, setIsImageLoading] = useState(false);

  useEffect(() => {
    const values = zoneValues[activeZone];
    if (values) {
      setCampArea(values.area);
      setCampNumberArray(values.camp_numbers);
      setMainGates(values.main_gate_number);
      setKitchens(values.kitchen_number);
      setBathrooms(values.bathroom_number);
      setAddressInput(values.address);
      setRadiusInput(values.radius);
      setImagePreview(values.sketch_image);
      setItems(values.items);
      if (values.latitude && values.longitude) {
        handleSetAddressMarker({
          lat: parseFloat(values.latitude),
          lng: parseFloat(values.longitude),
        });
      }
      setPdfFiles(
        values?.permits_and_contracts?.map(file => ({
          file: file.file,
          name: file.name,
          id: file.id,
          preview: file.pdf_file
            ? file.pdf_file
            : file.preview
            ? file.preview
            : URL.createObjectURL(file),
          description: file.description,
        })),
      );
    }
    setIsImageLoading(true);
  }, [activeZone]);

  useEffect(() => {
    if (activeZone) {
      const newZoneValues = { ...zoneValues };
      newZoneValues[activeZone] = {
        ...newZoneValues[activeZone],
        area: campArea,
        camp_numbers: campNumberArray,
        main_gate_number: mainGates,
        kitchen_number: kitchens,
        bathroom_number: bathrooms,
        address: addressInput,
        radius: radiusInput,
        sketch_image: imagePreview,
        items,
        latitude:
          typeof singleAddressMarker?.lat === 'function'
            ? singleAddressMarker?.lat()
            : singleAddressMarker?.lat,
        longitude:
          typeof singleAddressMarker?.lng === 'function'
            ? singleAddressMarker?.lng()
            : singleAddressMarker?.lng,
        permits_and_contracts: pdfFiles,
      };
      setZoneValues(newZoneValues);
    }
  }, [
    campArea,
    campNumberArray,
    mainGates,
    kitchens,
    bathrooms,
    addressInput,
    radiusInput,
    imagePreview,
    items,
    singleAddressMarker,
    pdfFiles,
  ]);

  useEffect(() => {
    if (campNumberArray?.length === 0) {
      setCampNumberArray(['']);
    }
  }, [campNumberArray]);

  const onCampAreaChange = e => {
    const newValue = e.target.value;
    setCampArea(newValue);
    let errors = { ...saveErrors };
    delete errors[`${activeZone}_camp_area`];
    setSaveErrors(errors);
  };

  const onCampNumberChange = ({ index, value }) => {
    const newCampNumberArray = [...campNumberArray];
    newCampNumberArray[index] = value;
    setCampNumberArray(newCampNumberArray);
  };

  const onMainGatesChange = e => {
    const newValue = e.target.value;
    setMainGates(newValue);
    let errors = { ...saveErrors };
    delete errors[`${activeZone}_main_gate_number`];
    setSaveErrors(errors);
  };

  const onKitchensChange = e => {
    const newValue = e.target.value;
    setKitchens(newValue);
    let errors = { ...saveErrors };
    delete errors[`${activeZone}_kitchen_number`];
    setSaveErrors(errors);
  };

  const onBathroomsChange = e => {
    const newValue = e.target.value;
    setBathrooms(newValue);
    let errors = { ...saveErrors };
    delete errors[`${activeZone}_bathroom_number`];
    setSaveErrors(errors);
  };

  const onAddressInputChange = e => {
    const { value } = e.target;
    setAddressInput(value);
    getPlacePredictions({
      input: value,
    });
    let errors = { ...saveErrors };
    delete errors[`${activeZone}_address`];
    setSaveErrors(errors);
  };

  const onRadiusInputChange = option => {
    if (option !== 'custom') {
      setRadiusInput(option);
      let errors = { ...saveErrors };
      delete errors[`${activeZone}_radius`];
      setSaveErrors(errors);
    }
  };

  return (
    <>
      <CommonGrid columns="30% 30% 30%">
        <InputWithError
          type="number"
          label={t('camp_area')}
          placeholder={t('type_camp_area')}
          value={campArea}
          onChange={onCampAreaChange}
          error={saveErrors?.[`${activeZone}_camp_area`]}
        />
        {activeZone !== 'Muzdalifah' && (
          <CommonGrid
            columns="30% 30% 30%"
            style={{
              width: '216%',
            }}
          >
            {campNumberArray?.map((item, index) => (
              <InputWithError
                label={`${t('camp_number')}${
                  index > 0 ? ` - 0${index + 1}` : ''
                }`}
                addMoreCampNumber={
                  index === 0 && campNumberArray.length < 3 && addMoreCampNumber
                }
                placeholder={t('type_camp_number')}
                value={campNumberArray[index]}
                onChange={e =>
                  onCampNumberChange({
                    index,
                    value: e.target.value,
                  })
                }
                error={saveErrors?.camp_number}
              />
            ))}
          </CommonGrid>
        )}
      </CommonGrid>
      {activeZone !== 'Muzdalifah' && (
        <CommonGrid columns="30% 30% 30%">
          <InputWithError
            type="number"
            label={t('number_of_main_gates')}
            placeholder={t('type_number_of_main_gates')}
            value={mainGates}
            onChange={onMainGatesChange}
            error={saveErrors?.[`${activeZone}_main_gate_number`]}
          />
          <InputWithError
            type="number"
            label={t('number_of_kitchens')}
            placeholder={t('type_number_of_kitchens')}
            value={kitchens}
            onChange={onKitchensChange}
            error={saveErrors?.[`${activeZone}_kitchen_number`]}
          />
          <InputWithError
            type="number"
            label={t('number_of_bathrooms')}
            placeholder={t('type_number_of_bathrooms')}
            value={bathrooms}
            onChange={onBathroomsChange}
            error={saveErrors?.[`${activeZone}_bathroom_number`]}
          />
        </CommonGrid>
      )}
      <CommonGrid columns="30% 66%">
        <div>
          {' '}
          <InputWithLabelSection
            style={{ position: 'relative' }}
            margin="20px 0 0 0"
          >
            <InputWithError
              type="text"
              label={t('office_address')}
              placeholder={t('type_here_to_search_address')}
              value={addressInput}
              onChange={onAddressInputChange}
              error={saveErrors?.[`${activeZone}_address`]}
            />
            {!isPredictionsLoading &&
            placePredictions &&
            placePredictions.length &&
            addressInput ? (
              <MapsAutocompleteDropdown
                dropdownOptions={placePredictions}
                handleSelect={getGeocode}
              />
            ) : null}
          </InputWithLabelSection>
          <InputWithLabelSection margin="20px 0 0 0">
            <CommonGrid>
              <InputLabel>
                {t('location_radius')}
                <RedStar>*</RedStar>
              </InputLabel>
            </CommonGrid>
            <CommonFlex wrap="wrap" justifyContent="start" gap="12px">
              {radiusOptions.map((option, i) => {
                const isCustom = option === 'custom';
                const isActive = isCustom
                  ? !radiusOptions.slice(0, -1).includes(radiusInput)
                  : radiusInput === option;

                return (
                  <RadiusButton
                    key={option}
                    active={isActive}
                    onClick={() => {
                      if (!isCustom) {
                        onRadiusInputChange(option);
                      }
                    }}
                  >
                    {isCustom ? t('custom') : `${option} m`}
                  </RadiusButton>
                );
              })}
            </CommonFlex>
            {saveErrors?.[`${activeZone}_radius`] && (
              <FormAlert margin="0 0 0 5px">
                {saveErrors[`${activeZone}_radius`]}
              </FormAlert>
            )}
          </InputWithLabelSection>
          <InputWithLabelSection margin="20px 0 0 0">
            <InputLabel>
              {t('site_sketch_image')}
              <RedStar>*</RedStar>
            </InputLabel>
            <PdfUploadSketch
              file={imagePreview ? imagePreview : null}
              setFile={setImagePreview}
            />
          </InputWithLabelSection>
        </div>
        {loadError ? (
          <div>Map cannot be loaded right now! Please try again later</div>
        ) : isLoaded ? (
          <MapAndAlertContainerForUpdateOffice>
            <OfficesMap
              currentPage="details"
              singleAddressMarker={singleAddressMarker}
              setAddressFromMap={handleSetAddressFromMap}
              setMap={setMap}
              radius={radiusInput}
              setRadius={setRadiusInput}
              isEditing={!!singleAddressMarker}
            />
          </MapAndAlertContainerForUpdateOffice>
        ) : (
          <ComponentCircleLoader />
        )}
      </CommonGrid>
      {activeZone !== 'Muzdalifah' && (
        <ItemsRelatedToCampsSection
          itemsList={itemsList}
          {...{ items, setItems, numberOfPilgrims }}
        />
      )}
      <InfoSection>
        <CommonText name title fontSize="16px">
          {t('permits_and_contracts')}
        </CommonText>
        <PdfUpload
          files={pdfFiles.length > 0 ? pdfFiles : []}
          setFiles={setPdfFiles}
          columns="30%"
        />
      </InfoSection>
    </>
  );
};

export default ZoneValues;
